// FindAngleFromThreeSidesTask.js
// Dane: trzy boki a, b, c. Oblicz kąty α, β, γ.
// Plan: (1) przekształcenie wzoru do postaci z cos; (2) α z tw. cosinusów;
//       (3) β z tw. cosinusów; (4) γ z sumy kątów.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  parseFractionLikeInput as parseFraction,
} from "../../../../components/inputs/FractionInput";

/* ===== helpers ===== */

const randInt = (lo, hi) => lo + Math.floor(Math.random() * (hi - lo + 1));
const toDeg = (rad) => (rad * 180) / Math.PI;

function preprocessRaw(raw) {
  if (raw == null) return "";
  return String(raw).trim().replace(/\u2212/g, "-").replace(/\s+/g, "").replace(",", ".");
}

function fmtDec(x, d = 3) {
  const s = Number(x).toFixed(d);
  return s.replace(".", "{,}");
}

function angleTripleFromSides(a, b, c) {
  // cos α = (b^2 + c^2 − a^2) / (2bc), analogicznie dla β; γ z sumy
  const A2 = a * a, B2 = b * b, C2 = c * c;
  const clamp = (t) => Math.min(1, Math.max(-1, t));
  const alpha = toDeg(Math.acos(clamp((B2 + C2 - A2) / (2 * b * c))));
  const beta  = toDeg(Math.acos(clamp((A2 + C2 - B2) / (2 * a * c))));
  const gamma = 180 - alpha - beta; // stabilniejsze niż trzeci arccos
  return { alpha, beta, gamma };
}

/* ===== generator ===== */

export function generateTask() {
  let a, b, c, alpha, beta, gamma;

  for (let t = 0; t < 300; t++) {
    a = randInt(6, 20);
    b = randInt(6, 20);
    c = randInt(Math.max(3, Math.abs(a - b) + 1), a + b - 1); // nierówność trójkąta

    const tri = angleTripleFromSides(a, b, c);
    alpha = tri.alpha;
    beta  = tri.beta;
    gamma = tri.gamma;

    // unikamy prawie-prostych i bardzo małych kątów
    const minAngle = Math.min(alpha, beta, gamma);
    if (Math.abs(alpha - 90) < 0.5 || Math.abs(beta - 90) < 0.5 || Math.abs(gamma - 90) < 0.5) continue;
    if (minAngle < 10) continue;

    break;
  }

  return {
    type: "FindAngleFromThreeSidesTask",
    data: {
      type: "FindAngleFromThreeSidesTask",
      a, b, c,
      alphaExact: alpha,
      betaExact: beta,
      gammaExact: gamma,
    },
  };
}

/* ===== treść ===== */

export function renderTask({ data }) {
  const { a, b, c } = data;
  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <div>
        W trójkącie <InlineMath math={'ABC'} /> dane są boki{" "}
        <InlineMath math={`a=${a}`} />, <InlineMath math={`b=${b}`} /> oraz{" "}
        <InlineMath math={`c=${c}`} />. Oblicz kąty{" "}
        <InlineMath math={'\\alpha,\\;\\beta,\\;\\gamma'} />.
      </div>
      <div style={{ marginTop: 6, opacity: 0.9 }}>
        Podaj wyniki z dokładnością do całych stopni.
      </div>
    </div>
  );
}

/* ===== input ===== */

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const val = userAnswer ?? { alpha: { raw: "" }, beta: { raw: "" }, gamma: { raw: "" } };
  const group = { display: "grid", gap: "6px" };

  return (
    <div style={{ display: "grid", gap: 12, marginTop: 6 }}>
      <div style={group}>
        <InlineMath math={'\\alpha ='} />
        <FractionInput
          value={val.alpha}
          onChange={(v) => onChange({ ...(userAnswer ?? {}), alpha: v })}
          disabled={disabled}
          label={"\\alpha"}
          suffixLatex="^\circ"     // pojedynczy backslash — jak ustaliliśmy
          placeholder="np. 58"
        />
      </div>

      <div style={group}>
        <InlineMath math={'\\beta ='} />
        <FractionInput
          value={val.beta}
          onChange={(v) => onChange({ ...(userAnswer ?? {}), beta: v })}
          disabled={disabled}
          label={"\\beta"}
          suffixLatex="^\circ"
          placeholder="np. 64"
        />
      </div>

      <div style={group}>
        <InlineMath math={'\\gamma ='} />
        <FractionInput
          value={val.gamma}
          onChange={(v) => onChange({ ...(userAnswer ?? {}), gamma: v })}
          disabled={disabled}
          label={"\\gamma"}
          suffixLatex="^\circ"
          placeholder="np. 58"
        />
      </div>
    </div>
  );
}

/* ===== check ===== */

export function checkAnswer({ data }, userAnswer) {
  const { alphaExact, betaExact, gammaExact } = data;

  const aRaw = typeof userAnswer?.alpha === "object" ? userAnswer.alpha?.raw : userAnswer?.alpha;
  const bRaw = typeof userAnswer?.beta  === "object" ? userAnswer.beta?.raw  : userAnswer?.beta;
  const gRaw = typeof userAnswer?.gamma === "object" ? userAnswer.gamma?.raw : userAnswer?.gamma;

  const aVal = aRaw == null || String(aRaw).trim() === "" ? null : parseFraction(preprocessRaw(aRaw));
  const bVal = bRaw == null || String(bRaw).trim() === "" ? null : parseFraction(preprocessRaw(bRaw));
  const gVal = gRaw == null || String(gRaw).trim() === "" ? null : parseFraction(preprocessRaw(gRaw));

  // jeśli ktoś nic nie wpisał
  const vals = [aVal, bVal, gVal].filter(v => v != null && Number.isFinite(v));
  if (vals.length === 0) {
    return { isCorrect: false, score: 0, maxScore: 2, parts: { any: false, all: false } };
  }

  const A = Math.round(alphaExact);
  const B = Math.round(betaExact);
  const G = Math.round(gammaExact);

  const tol = 1; // ±1°
  const expected = [A, B, G];

  // liczymy, ile unikalnych kątów z expected da się dopasować do wpisanych wartości (dowolna kolejność)
  const matched = new Array(expected.length).fill(false);

  for (const v of vals) {
    for (let i = 0; i < expected.length; i++) {
      if (matched[i]) continue;
      if (Math.abs(v - expected[i]) <= tol) {
        matched[i] = true;
        break;
      }
    }
  }

  const count = matched.filter(Boolean).length;

  const score = count >= 3 ? 2 : count >= 1 ? 1 : 0;

  return {
    isCorrect: score === 2,
    score,
    maxScore: 2,
    parts: {
      any: count >= 1,
      all: count >= 3,
    },
  };
}

/* ===== answer ===== */

export function renderAnswer({ data }) {
  const { alphaExact, betaExact, gammaExact } = data;

  const A = Math.round(alphaExact);
  const B = Math.round(betaExact);
  const G = Math.round(gammaExact);

  const exactA = Math.abs(alphaExact - A) < 1e-9;
  const exactB = Math.abs(betaExact - B)  < 1e-9;
  const exactG = Math.abs(gammaExact - G) < 1e-9;

  const aLatex = exactA ? `\\alpha = ${A}^\\circ` : `\\alpha \\approx ${A}^\\circ`;
  const bLatex = exactB ? `\\beta = ${B}^\\circ`  : `\\beta \\approx ${B}^\\circ`;
  const gLatex = exactG ? `\\gamma = ${G}^\\circ` : `\\gamma \\approx ${G}^\\circ`;

  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <InlineMath math={`${aLatex},\\; ${bLatex},\\; ${gLatex}`} />
    </div>
  );
}

/* ===== explanation ===== */

export function renderExplanation({ data }) {
  const { a, b, c, alphaExact, betaExact } = data;

  const A2 = a * a, B2 = b * b, C2 = c * c;

  const numG = A2 + B2 - C2, denG = 2 * a * b;
  const numA = B2 + C2 - A2, denA = 2 * b * c;
  const numB = A2 + C2 - B2, denB = 2 * a * c;

  const cosA = numA / denA;
  const cosB = numB / denB;

  const A = Math.round(alphaExact);
  const B = Math.round(betaExact);
  const G = 180 - A - B;

  return (
    <div style={{ textAlign: "left", marginTop: 8, lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> Przekształcenie twierdzenia cosinusów do postaci z <InlineMath math={'\\cos'} />:</div>
      <BlockMath math={`c^{2} = a^{2} + b^{2} - 2ab\\cos\\gamma`} />
      <BlockMath math={`2ab\\cos\\gamma = a^{2} + b^{2} - c^{2}`} />
      <BlockMath math={`\\Rightarrow\\; \\cos\\gamma = \\dfrac{a^{2} + b^{2} - c^{2}}{2ab}`} />
      <div style={{ height: 6 }} />

      <div><strong>Krok 2.</strong> Liczymy kąt <InlineMath math={'\\alpha'} /> z twierdzenia cosinusów:</div>
      <BlockMath math={`\\cos\\alpha = \\dfrac{b^{2} + c^{2} - a^{2}}{2bc}`} />
      <BlockMath math={`= \\dfrac{${B2} + ${C2} - ${A2}}{2\\cdot ${b}\\cdot ${c}} = \\dfrac{${numA}}{${denA}} \\approx ${fmtDec(cosA, 3)}`} />
      <BlockMath math={`\\cos\\alpha \\approx ${fmtDec(cosA, 3)}\\;\\Rightarrow\\; \\alpha \\approx ${A}^\\circ`} />

      <div><strong>Krok 3.</strong> Liczymy kąt <InlineMath math={'\\beta'} /> z twierdzenia cosinusów:</div>
      <BlockMath math={`\\cos\\beta = \\dfrac{a^{2} + c^{2} - b^{2}}{2ac}`} />
      <BlockMath math={`= \\dfrac{${A2} + ${C2} - ${B2}}{2\\cdot ${a}\\cdot ${c}} = \\dfrac{${numB}}{${denB}} \\approx ${fmtDec(cosB, 3)}`} />
      <BlockMath math={`\\cos\\beta \\approx ${fmtDec(cosB, 3)}\\;\\Rightarrow\\; \\beta \\approx ${B}^\\circ`} />

      <div><strong>Krok 4.</strong> Trzeci kąt z sumy kątów trójkąta:</div>
      <BlockMath math={`\\gamma = 180^\\circ - \\alpha - \\beta \\approx 180^\\circ - ${A}^\\circ - ${B}^\\circ = ${G}^\\circ`} />

      <div style={{ marginTop: 6 }}>
        <strong>Odpowiedź:</strong>{" "}
        <BlockMath math={`\\alpha \\approx ${A}^\\circ,\\; \\beta \\approx ${B}^\\circ,\\; \\gamma \\approx ${G}^\\circ`} />
      </div>
    </div>
  );
}

/* ===== user answer preview ===== */

function pretty(x) {
  const s = String(x ?? "").trim();
  if (!s) return "";
  return s.replace(/\s+/g, "").replace(".", "{,}");
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;
  const aRaw = typeof userAnswer.alpha === "object" ? userAnswer.alpha?.raw : userAnswer.alpha;
  const bRaw = typeof userAnswer.beta  === "object" ? userAnswer.beta?.raw  : userAnswer.beta;
  const gRaw = typeof userAnswer.gamma === "object" ? userAnswer.gamma?.raw : userAnswer.gamma;

  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <InlineMath math={`\\displaystyle \\alpha = ${pretty(aRaw)}^\\circ`} />
      <span style={{ margin: "0 8px" }} />
      <InlineMath math={`\\displaystyle \\beta = ${pretty(bRaw)}^\\circ`} />
      <span style={{ margin: "0 8px" }} />
      <InlineMath math={`\\displaystyle \\gamma = ${pretty(gRaw)}^\\circ`} />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
